import { useRef, useState } from 'react';
import { Icone } from '../Icone';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { useGesteAbonnement, type EtatAbonnement } from '../../lib/useCalendrier';

/**
 * Abonnement de l'agenda personnel au planning (TF-452).
 *
 * L'apprenant ou le formateur ajoute son planning à Google Agenda, Outlook ou
 * Apple par un lien iCal : l'agenda le relit de lui-même, un changement de
 * planning y passe sans rien refaire. Les liens d'ajout viennent de l'API ;
 * l'écran ne fait que les proposer, avec les deux gestes qui protègent le lien
 * — le renouveler s'il a pu circuler, le désactiver.
 */

type Confirmation = 'RENOUVELER' | 'DESACTIVER' | null;

function dateHeure(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Indian/Reunion',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

const CONTENU_PAR_ESPACE = {
  apprenant: 'vos séances, vos jours au centre, vos épreuves et ECF, et les échéances de votre dossier',
  formateur: 'les séances que vous animez',
} as const;

export function AbonnementAgenda({
  etat,
  espace,
}: {
  etat: EtatAbonnement;
  espace: keyof typeof CONTENU_PAR_ESPACE;
}): JSX.Element {
  const notifier = useToast();
  const geste = useGesteAbonnement();
  const champ = useRef<HTMLInputElement>(null);
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  const abonnement = etat.abonnement;
  const liens = abonnement?.liens ?? null;

  const agir = (choix: 'CREER' | 'RENOUVELER' | 'DESACTIVER'): void => {
    geste.mutate(choix, {
      onSuccess: () => {
        setConfirmation(null);
        notifier(
          choix === 'CREER'
            ? 'Lien d’abonnement créé'
            : choix === 'RENOUVELER'
              ? 'Lien renouvelé : l’ancien ne fonctionne plus'
              : 'Abonnement désactivé',
        );
      },
      onError: (erreur) => {
        setConfirmation(null);
        notifier(erreur instanceof ErreurApi ? erreur.message : 'Le geste n’a pas pu aboutir');
      },
    });
  };

  const copier = async (): Promise<void> => {
    if (!liens) return;
    try {
      await navigator.clipboard.writeText(liens.ical);
      notifier('Lien copié');
    } catch {
      // Sans presse-papiers (page non sécurisée, refus du navigateur), le lien est sélectionné.
      champ.current?.select();
      notifier('Lien sélectionné : copiez-le avec Ctrl+C');
    }
  };

  if (etat.refus) {
    return (
      <div className="p-4 sm:p-6">
        <MessageErreur message={etat.refus} />
      </div>
    );
  }

  const introduction = (
    <p className="text-sm text-codialis-navy">
      Votre agenda habituel affiche {CONTENU_PAR_ESPACE[espace]}, et se met à jour tout seul quand le
      planning change ici.
    </p>
  );

  if (!abonnement) {
    return (
      <div className="space-y-4 p-4 sm:p-6">
        {introduction}
        <p className="text-xs text-codialis-gray">
          Le lien créé est personnel : quiconque le possède voit votre planning, sans mot de passe.
        </p>
        <div className="flex justify-end">
          <BoutonPrincipal onClick={() => agir('CREER')} disabled={geste.isPending}>
            {geste.isPending ? 'Création…' : 'Créer mon lien d’abonnement'}
          </BoutonPrincipal>
        </div>
      </div>
    );
  }

  const agendas: [string, string, boolean][] = liens
    ? [
        ['Google Agenda', liens.google, true],
        ['Outlook.com', liens.outlook, true],
        ['Outlook (Microsoft 365)', liens.outlook365, true],
        ['Apple Calendrier', liens.webcal, false],
      ]
    : [];

  return (
    <div className="space-y-5 p-4 sm:p-6">
      {introduction}

      {liens ? (
        <>
          <section>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-codialis-gray">
              Ajouter à votre agenda
            </h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {agendas.map(([libelle, adresse, nouvelOnglet]) => (
                <a
                  key={libelle}
                  href={adresse}
                  {...(nouvelOnglet ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className="flex min-h-[44px] min-w-0 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
                >
                  <Icone nom="calendrier" className="h-4 w-4 shrink-0 text-codialis-blue" />
                  <span className="truncate">{libelle}</span>
                </a>
              ))}
            </div>
          </section>

          <section>
            <label
              htmlFor="lien-abonnement"
              className="mb-2 block text-[11px] font-bold uppercase tracking-wide text-codialis-gray"
            >
              Ou copiez le lien dans votre agenda
            </label>
            <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
              <input
                id="lien-abonnement"
                ref={champ}
                readOnly
                value={liens.ical}
                onFocus={(evenement) => evenement.target.select()}
                className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 font-mono text-xs text-codialis-navy"
              />
              <BoutonSecondaire onClick={() => void copier()}>
                <Icone nom="copier" className="mr-2 h-4 w-4" />
                Copier
              </BoutonSecondaire>
            </div>
          </section>
        </>
      ) : (
        <MessageErreur message="Ce lien ne peut plus être affiché. Renouvelez-le pour en obtenir un nouveau, puis réabonnez votre agenda." />
      )}

      <div className="space-y-1 rounded-xl bg-codialis-sky/5 px-3 py-2.5 text-xs text-codialis-navy">
        <p>
          Google relit les agendas abonnés à son rythme : un changement peut mettre jusqu’à 24 h à y
          apparaître. Outlook et Apple le relisent en général en quelques heures.
        </p>
        <p className="text-codialis-gray">
          Lien créé le {dateHeure(abonnement.renouveleLe ?? abonnement.creeLe)} ·{' '}
          {abonnement.dernierAccesLe
            ? `dernière lecture par un agenda le ${dateHeure(abonnement.dernierAccesLe)}`
            : 'aucun agenda ne l’a encore lu'}
        </p>
      </div>

      {confirmation ? (
        <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm text-codialis-navy">
            {confirmation === 'RENOUVELER'
              ? 'L’ancien lien cessera aussitôt de fonctionner : il faudra réabonner votre agenda avec le nouveau.'
              : 'Votre agenda ne recevra plus votre planning. Vous pourrez créer un nouveau lien plus tard.'}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <BoutonSecondaire onClick={() => setConfirmation(null)} disabled={geste.isPending}>
              Annuler
            </BoutonSecondaire>
            <BoutonPrincipal onClick={() => agir(confirmation)} disabled={geste.isPending}>
              {geste.isPending
                ? 'Un instant…'
                : confirmation === 'RENOUVELER'
                  ? 'Renouveler le lien'
                  : 'Désactiver l’abonnement'}
            </BoutonPrincipal>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-codialis-gray">
            Lien personnel : ne le partagez pas. S’il a pu circuler, renouvelez-le.
          </p>
          <div className="flex flex-wrap gap-2 sm:shrink-0">
            <BoutonSecondaire onClick={() => setConfirmation('RENOUVELER')}>Renouveler</BoutonSecondaire>
            <button
              type="button"
              onClick={() => setConfirmation('DESACTIVER')}
              className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50"
            >
              Désactiver
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
